// Basic
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cn } from '@/common-utils/libraries/tailwind/utils';
import { useTranslation } from 'react-i18next';
// Store & actions
import actionsOrganizations from '../../../api/actionsOrganizations';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FormAddOrganization from './FormAddOrganization';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import LegalInfosPanel from '../../details/LegalInfosPanel';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import RouteUrls from '@/app/root/enums/RouteUrls';
import { buildUrl, replaceParameters } from '@/common-utils/functions/restUtils';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.currentOrganizationId
 * @returns {JSX.Element} ModalAddOrganization component
 */
function ModalAddOrganization({ isOpen, setIsOpen, currentOrganizationId }) {

    // Const
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.add_organization.steps';

    // Translation
    const { t: translate } = useTranslation('organization');

    // Toast
    const { toast } = useToast();

    // Redirection
    const navigate = useNavigate();

    // State
    const [step, setStep] = useState(0);
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [organizationFormData, setOrganizationFormData] = useState('');

    // Registration number form
    const validateRegistrationNumberFormSchema = z.object({
        registrationNumber: z.string()
            .min(14, translate('form.error.INVALID_REGISTRATION_NUMBER'))
            .max(14, translate('form.error.INVALID_REGISTRATION_NUMBER'))
            .refine(
                (value) => {
                    if (value && value.length === 14) {
                        const isValidSiret = value
                            .split('')
                            .reduce(
                                (acc, digit, index) => {
                                    const tmp = Number(digit) * (2 - (index % 2));
                                    return acc + (tmp > 9 ? tmp - 9 : tmp);
                                },
                                0
                            ) % 10 === 0;
                        return isValidSiret;
                    }
                    return false;
                },
                {
                    message: translate('form.error.INVALID_REGISTRATION_NUMBER')
                }
            )
    });
    const registrationNumberForm = useForm({
        resolver: zodResolver(validateRegistrationNumberFormSchema),
        mode: 'onChange',
        defaultValues: { registrationNumber: '' }
    });

    const { data: organizationData, isFetching, refetch } = actionsOrganizations.checkRegistrationNumber(registrationNumber, false);

    const onSuccess = ({ data }) => {
        const organizationUrl = buildUrl(replaceParameters(RouteUrls.ORGANIZATION_DETAILS.url, { organizationId: data.uuid }), { view: 'details' });

        navigate(organizationUrl);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.add_organization.success' namespace={nsOrganization} />,
            variant: 'success'
        });
    };
    const onError = (error) => {
        const errorCode = error.response.data.errorCode === 'ORGANIZATION_ALREADY_EXISTS' ? 'ORGANIZATION_ALREADY_EXISTS' : 'UNKNOWN_ERROR';

        setIsOpen(false);
        toast({
            description: <Entity entity={`modal.errors.${errorCode}`} namespace={nsOrganization} />,
            variant: 'danger'
        });
    };
    const addOrganization = actionsOrganizations.addOrganization(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: { stepTitle: `${STEP_TITLE_PREFIX}.registration_number`, disabledNext: !registrationNumberForm.formState.isValid },
            1: { stepTitle: `${STEP_TITLE_PREFIX}.organization_info`, disabledNext: !organizationFormData.isValid }
        }),
        [organizationFormData, registrationNumberForm.formState.isValid]
    );


    useEffect(() => {
        setRegistrationNumber(registrationNumberForm.getValues('registrationNumber'));

        if (step === 1 && registrationNumber) {
            refetch(true, registrationNumber);
        }
    }, [registrationNumber, registrationNumberForm.watch('registrationNumber')]);

    // Functions
    const handleValidation = () => {
        let organizationInfos = organizationFormData.data;

        // Set legalName from organization data if available
        if (organizationData?.companyLongName) {
            organizationInfos = { ...organizationInfos, legalName: organizationData?.companyLongName };
        }

        addOrganization.mutate({
            organizationId: currentOrganizationId,
            organizationInfos: organizationInfos
        });
    };

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={cn('max-w-lg')}>
                <HeaderTitle title='modal.add_organization.title' namespace={nsOrganization} />
                <ModalBody>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsOrganization}
                        className={'mb-2 flex'}
                    />
                    {step === 0 &&
                        <Form {...registrationNumberForm} >
                            <FormField
                                name='registrationNumber'
                                control={registrationNumberForm.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel required>
                                            <Entity namespace={nsOrganization} entity='modal.add_organization.registration_number' />
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type='tel'
                                                maxLength='14'
                                                autoComplete='off'
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </Form>
                    }
                    {step === 1 &&
                        <>
                            {isFetching ? <Spinner className='mt-4' />
                                :
                                <>
                                    <FormAddOrganization
                                        registrationNumber={registrationNumber}
                                        organizationData={organizationData}
                                        setOrganizationFormData={setOrganizationFormData}
                                    />
                                    {organizationData &&
                                        <LegalInfosPanel
                                            className='mt-4 mb-4 max-h-72 border rounded-md overflow-y-auto text-sm'
                                            showTitle={false}
                                            legalInfos={organizationData}
                                        />
                                    }
                                </>
                            }
                        </>
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={handleValidation}
                    isLoading={addOrganization.isLoading}
                    disableValidation={disabledNext}
                />
            </ModalContent>
        </Modal >
    );
}

ModalAddOrganization.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    currentOrganizationId: PropTypes.string.isRequired
};

export default ModalAddOrganization;